import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner, Typography } from '@telus-uds/components-web';

import Modal from '../shared/modal';

import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import {
  StatusBarWrapper,
  BoxWrapper,
  DueMonthlyWrapper,
  LabelDueMonthly,
  PricingBlockMain,
  BeforeTaxesLabel,
  DueNowWrapper,
  IntegralPart,
  ModalBottomContainer,
  DownloadQuoteMessageContainer,
  VerticalWrapper,
  HorizontalWrapper
} from './css';
import { formatCurrency } from '../../utils/currencyFormatter';
import { DiscountApplicationChip, DiscountApplicationChipNotch } from '../../form/css';

const getFormattedPricing = price => {
  return <IntegralPart>{formatCurrency(price)}</IntegralPart>;
};

const FmStatusBar = ({
  step,
  isLastStep,
  isFormTouched,
  onSaveAndExit,
  onProceedClick,
  onBackClick,
  showDownloadQuoteFooter,
  totalMonthly,
  totalOneTime,
  onQuoteDownload,
  isFormSubmit,
  isCSForm,
  isDiscountApplied,
  quotePdfStatus
}) => {
  const localizedStrings = useLocalizedStringsContext();

  const {
    formButtons: {
      continueButtonLabel,
      backLabel,
      saveContinueLaterButtonLabel,
      submitButtonLabel
    }
  } = localizedStrings;

  const [
    isDownloadQuoteModalVisible,
    setIsDownloadQuoteModalVisible
  ] = useState(false);

  const onCloseDownloadQuoteModal = () => {
    setIsDownloadQuoteModalVisible(false);
  };

  const showDownloadQuoteModal = () => {
    setIsDownloadQuoteModalVisible(true);
  };

  return (
    <StatusBarWrapper>
      <div id="footer-wrapper">
        <BoxWrapper>
          <VerticalWrapper>
            <HorizontalWrapper>
              <DueMonthlyWrapper>
                <div>
                  <LabelDueMonthly>Due Monthly</LabelDueMonthly>
                  <PricingBlockMain>
                    {getFormattedPricing(totalMonthly)}
                  </PricingBlockMain>
                  <BeforeTaxesLabel>Before Taxes</BeforeTaxesLabel>
                </div>
              </DueMonthlyWrapper>
              <DueNowWrapper>
                <>
                  <div>
                    <LabelDueMonthly>Due Now</LabelDueMonthly>
                    <PricingBlockMain>
                      {getFormattedPricing(totalOneTime)}
                    </PricingBlockMain>
                    <BeforeTaxesLabel>Before Taxes</BeforeTaxesLabel>
                  </div>
                </>
              </DueNowWrapper>
            </HorizontalWrapper>
            {
              isDiscountApplied && 
              <DiscountApplicationChip>
                <DiscountApplicationChipNotch/>
                Discount Applied
              </DiscountApplicationChip>
            }
          </VerticalWrapper>
          {showDownloadQuoteFooter ? (
            <>
              <Spinner label="Preparing PDF…" variant={{ size: 'small' }} show={quotePdfStatus === 'PREPARING'} inline>
                <Button
                  type="button"
                  disabled={quotePdfStatus === 'PREPARING'}
                  onClick={() => {
                    showDownloadQuoteModal();
                    onQuoteDownload();
                  }}
                  variant={{ priority: 'high' }}
                >
                 {isCSForm ? "Download the quote (PDF)" : "Download the proposal (PDF)"}
                </Button>
              </Spinner>
              {quotePdfStatus === 'FAILED' && (
                <Typography variant={{ colour: 'error' }}>Download failed — please try again.</Typography>
              )}
              <Button
                type="button"
                onClick={e => onProceedClick(e)}
                variant={{ priority: 'high' }}
              >
                Proceed with the order
              </Button>
            </>
          ) : (
            <>
              {step !== 1 && (
                <Button variant={{ priority: 'high' }} type="button"
                  onClick={onBackClick}>
                  {backLabel}
                </Button>
              )}
              <Button
                type="button"
                onClick={e => onSaveAndExit(e)}
                variant={{ priority: 'high' }}
                disabled={!isFormTouched}
              >
                {saveContinueLaterButtonLabel}
              </Button>
              <Button
                disabled={isLastStep && !isFormSubmit}
                type="button"
                onClick={e => onProceedClick(e)}
                variant={{ priority: 'high' }}
              >
                {isLastStep ? submitButtonLabel : continueButtonLabel}
              </Button>
            </>
          )}
        </BoxWrapper>
      </div>
      {!isCSForm && (
        <Modal
          open={isDownloadQuoteModalVisible}
          setOpen={onCloseDownloadQuoteModal}
          size="large"
          header="Download Proposal PDF"
        >
          <DownloadQuoteMessageContainer>
            Monthly Total value {getFormattedPricing(totalMonthly)}. Please
            update your Salesforce opportunity accordingly.
          </DownloadQuoteMessageContainer>
          <ModalBottomContainer>
            <Button onClick={onCloseDownloadQuoteModal} variant={{ priority: 'low' }}>
              Close
            </Button>
          </ModalBottomContainer>
        </Modal>
      )}
    </StatusBarWrapper>
  );
};

FmStatusBar.propTypes = {
  step: PropTypes.number.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  onSaveAndExit: PropTypes.func.isRequired,
  onProceedClick: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  showDownloadQuoteFooter: PropTypes.bool.isRequired,
  totalMonthly: PropTypes.number.isRequired,
  totalOneTime: PropTypes.number.isRequired,
  onQuoteDownload: PropTypes.func.isRequired,
  isDiscountApplied: PropTypes.bool.isRequired,
  isFormSubmit: PropTypes.bool.isRequired,
  quotePdfStatus: PropTypes.string
};


export default FmStatusBar;
